<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { MAX_SITE_EXCLUSIONS, SITE_API_DEBOUNCE_WAIT_MS } from '@/constants';
import { DgrCheckbox } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import SiteSelect from '@/components/theme/edit/site-select.vue';
import { Site } from '@/types';
import { debounce } from '@/utils/debounce';
import { useUserSettingsNews } from '@/utils/swr';

const route = useRoute();
const router = useRouter();
const { createSnackbar } = useSnackbar();
const { data: userSettings, mutate } = useUserSettingsNews();

const enablePersonalNewsEntertainmentFilter = ref(false);
const excludeSiteKeyword = ref('');
const selectedSites = ref<Site[]>([]);
const sites = ref<Site[]>([]);
const isSaving = ref(false);

watch(
  userSettings,
  settings => {
    if (!settings) return;
    enablePersonalNewsEntertainmentFilter.value =
      settings.enable_personal_news_entertainment_filter;
    selectedSites.value = (settings.personal_feeds_excluded_sites ?? []).map(
      s => ({ ...s }),
    );
  },
  { immediate: true },
);

const fetchSites = async () => {
  try {
    sites.value = await api.getSites(
      false,
      null,
      MAX_SITE_EXCLUSIONS,
      excludeSiteKeyword.value || null,
      selectedSites.value.map(s => s.id),
    );
  } catch (err) {
    sites.value = [];
    throw err;
  }
};

watch(
  [
    excludeSiteKeyword,
    () => selectedSites.value.map(site => site.id).join(','),
  ],
  debounce(() => {
    fetchSites();
  }, SITE_API_DEBOUNCE_WAIT_MS),
  { immediate: true },
);

const cancel = () => {
  if (window.history.length > 0 && window.history.state.back !== null) {
    router.back();
    return;
  }
  router.push({ name: 'personalNews' });
};

const save = async () => {
  if (!userSettings.value || isSaving.value) return;
  isSaving.value = true;

  try {
    await api.updateUserSettingsNews(
      {
        ...userSettings.value,
        enable_personal_news_entertainment_filter:
          enablePersonalNewsEntertainmentFilter.value,
        personal_feeds_excluded_site_ids: selectedSites.value.map(s => s.id),
      },
      {
        pageName: 'news',
        pageUrl: route.fullPath,
      },
    );
    await mutate();
    createSnackbar({
      message: '設定を保存しました。',
      description: '7:00または13:00の配信から反映されます。',
      type: 'success',
    });
    router.push({ name: 'personalNews' });
  } catch (err) {
    createSnackbar({
      message: '設定を更新できませんでした',
      type: 'error',
    });
    throw err;
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="personal-news-edit">
    <!-- INFO: content-widthはTheme編集画面と揃えるため -->
    <Header
      title="パーソナルニュース設定"
      content-width="1360px"
      :align-left="true"
      :is-one-line="false"
    >
      <template #button>
        <div class="header-buttons">
          <button
            class="c-btn c-btn--auto c-btn--small c-btnOutline"
            @click="cancel"
          >
            キャンセル
          </button>
          <button
            class="c-btn c-btn--auto c-btn--small c-btn--AnewsPrimary"
            :class="{ disabled: !userSettings || isSaving }"
            :disabled="!userSettings || isSaving"
            @click="save"
          >
            保存
          </button>
        </div>
      </template>
    </Header>

    <div v-if="userSettings" class="personal-news-edit__wrapper">
      <div class="edit-content">
        <div class="settings-card">
          <div class="settings-title c-title-bold--s">ニュース配信設定</div>

          <div class="entertainment-filter">
            <DgrCheckbox
              v-model="enablePersonalNewsEntertainmentFilter"
              class="c-text-regular--m"
            >
              エンタメ系ニュースの配信停止
            </DgrCheckbox>
            <div
              class="settings-explanation entertainment-explanation c-text-regular--s"
            >
              パーソナルニュースにエンタメ系ニュースが配信されなくなります。
            </div>
          </div>

          <div class="exclude-media">
            <div class="c-title-bold--m">除外メディア</div>
            <div class="settings-explanation c-text-regular--s">
              配信されるニュースから除外したいメディアを100件まで登録することができます。
            </div>
            <SiteSelect
              v-model="selectedSites"
              v-model:keyword="excludeSiteKeyword"
              :items="sites"
              :max-items="MAX_SITE_EXCLUSIONS"
              :has-next-exclude-sites="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.personal-news-edit {
  width: 100%;

  .header-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;
  }

  .personal-news-edit__wrapper {
    margin: 24px 24px 0 24px;
  }

  .edit-content {
    width: 100%;
  }

  .settings-card {
    box-sizing: border-box;
    width: 440px;
    padding: 16px;
    background: $color-white;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .settings-title {
    color: $color-gray1000;
  }

  .entertainment-filter {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .exclude-media {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .settings-explanation {
    color: $color-gray600;
    line-height: 16px;
  }

  .entertainment-explanation {
    padding-left: 20px;
    width: 408px;
  }
}
</style>
